import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  load,
  IsLoadingProducts,
  SelectOrderedBy,
  IsShowingActives,
  setIsLoadingProducts,
  IsLoadingExternalBilling,
  IsLoadingExternalProducts,
  IsLoadedProducts,
  SelectNeedRefreshProducts,
} from "../redux/reducers/products.reducer";
import { ProductsService } from "./../services/products";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import { useNavigate } from "react-router";
import { ROUTE_HOME } from "../../SharedModule/utils/constants";
import { Product } from "../types/products-module-types";

export const useProducts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoadingProducts = useSelector(IsLoadingProducts);
  const isLoadedProducts = useSelector(IsLoadedProducts);
  const needRefreshProducts = useSelector(SelectNeedRefreshProducts);
  const isLoadingExternalBilling = useSelector(IsLoadingExternalBilling);
  const isLoadingExternalProducts = useSelector(IsLoadingExternalProducts);
  const isShowingActives = useSelector(IsShowingActives);
  const { list: products } = useSelector(SelectOrderedBy);

  const productsToShow: Array<Product> = products
    .filter(
      (prod) => prod.status.name === (isShowingActives ? "Active" : "Deleted")
    )
    .sort((a, b) =>
      a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase())
    );

  const { rolesLoaded } = useSelector(selectUserRoles);

  const loadProducts = useCallback(async () => {
    if (rolesLoaded && (!isLoadedProducts || needRefreshProducts)) {
      dispatch(setIsLoadingProducts(true));
      try {
        const products = await ProductsService.getAllProducts();
        dispatch(load(products));
      } catch (error: any) {
        dispatch(
          addToast({
            mode: "error",
            message: error?.message
              ? `Error: ${error.message}`
              : "Something wrong happened while getting products information.",
          })
        );
        dispatch(setIsLoadingProducts(false));
        navigate(ROUTE_HOME);
        return;
      }
    }
  }, [dispatch, rolesLoaded, needRefreshProducts]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  return {
    products,
    productsToShow,
    isLoadingProducts,
    isShowingActives,
    loadProducts,
    isLoadingExternalBilling,
    isLoadingExternalProducts,
  };
};
